// تحدي الإدارات — منافسة عادلة بالنسب والمتوسطات ورسائل تحفيزية فقط
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { GlassCard, Kpi, Chip, Skeleton, PageHead, Bar, Empty } from "@/v2/admin/ui";
import { useDept } from "./DeptShell";
import {
  Swords, Trophy, TrendingUp, Users, Gauge, Target, Sparkles, ArrowUpRight, Medal,
} from "lucide-react";

const PERIODS = [["week", "هذا الأسبوع"], ["month", "هذا الشهر"], ["overall", "الترتيب العام"]];

export default function Competition() {
  const { q } = useDept();
  const [period, setPeriod] = useState("overall");
  const { data, isLoading } = useQuery({
    queryKey: ["dept-competition", period, q],
    queryFn: async () => (await api.get(`/dept/competition?period=${period}&${q}`)).data,
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  const my = data?.my;
  const ranking = data?.ranking || [];

  return (
    <div className="space-y-6" data-testid="dept-competition">
      <PageHead icon={Swords} title="تحدي الإدارات" sub="ترتيب عادل يعتمد على النسب والمتوسطات — لا يتأثر بعدد الموظفين.">
        <div className="flex gap-2">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`comp-period-${k}`}
              className={`v2-btn-sm ${period === k ? "v2-btn" : "v2-btn-ghost"}`}>{l}</button>
          ))}
        </div>
      </PageHead>

      {!my ? <Empty text="لا توجد بيانات كافية لعرض المنافسة بعد." /> : (
        <>
          <GlassCard gold className="p-6 sm:p-8 v2-grid v2-pattern relative overflow-hidden" data-testid="comp-my-card">
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              <div className="text-center shrink-0">
                <div className="w-24 h-24 rounded-full bg-[#bb9661]/15 border-2 border-[#bb9661]/40 flex flex-col items-center justify-center">
                  <Trophy size={22} className="text-[#e3c799]" />
                  <div className="text-2xl font-extrabold v2-gold-text">#{my.rank}</div>
                </div>
                <div className="text-[11px] text-[#bcc8cf] mt-2">من {data.count} إدارة</div>
              </div>
              <div className="flex-1 text-center md:text-start">
                <h2 className="text-xl md:text-lg font-extrabold mb-1">إدارة {my.name}</h2>
                <div className="flex items-center gap-2 justify-center md:justify-start mb-3 flex-wrap">
                  <Chip tone="gold"><Medal size={12} /> النتيجة {my.score} / 100</Chip>
                  <Chip tone="emerald"><Users size={12} /> {my.employees} موظفًا</Chip>
                  {my.improvement > 0 && <Chip tone="emerald"><TrendingUp size={12} /> +{my.improvement}% تحسّن</Chip>}
                </div>
                <Bar value={my.score} />
                <p className="mt-4 text-base font-bold v2-emerald-text" data-testid="comp-message">{my.message}</p>
                {my.gap_to_next > 0 && (
                  <p className="text-xs text-[#bcc8cf] mt-1 flex items-center gap-1 justify-center md:justify-start">
                    <ArrowUpRight size={13} /> يفصلكم {my.gap_to_next} نقطة عن {my.next_name || "المركز التالي"}
                  </p>
                )}
              </div>
            </div>
          </GlassCard>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Kpi icon={Target} label="نسبة الإنجاز" value={my.completion_rate} suffix="%" testId="comp-kpi-completion" />
            <Kpi icon={Gauge} label="معدل المشاركة" value={my.participation_rate} suffix="%" tone="gold" />
            <Kpi icon={Sparkles} label="متوسط نقاط الموظف النشط" value={my.avg_points_active} />
            <Kpi icon={TrendingUp} label="نسبة التحسّن" value={my.improvement} suffix="%" tone="gold" />
          </div>

          <GlassCard className="p-5" data-testid="comp-ranking">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-bold flex items-center gap-2"><Trophy size={16} className="text-[#e3c799]" /> ترتيب الإدارات</h3>
              <span className="text-[11px] text-[#bcc8cf]">
                الأوزان: إنجاز {data.weights.completion}% · مشاركة {data.weights.participation}% · متوسط النقاط {data.weights.points}% · تحسّن {data.weights.improvement}%
              </span>
            </div>
            <div className="space-y-2">
              {ranking.map((r) => (
                <div key={r.name} data-testid={`comp-rank-${r.rank}`}
                  className={`flex items-center gap-3 p-3 rounded-2xl border ${
                    r.is_mine ? "border-[#bb9661]/50 bg-[#bb9661]/10" : "border-white/8 bg-white/[0.03]"}`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold shrink-0 ${
                    r.rank === 1 ? "bg-[#bb9661] text-[#25343f]" : "bg-white/5 text-[#bcc8cf]"}`}>{r.rank}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{r.name} {r.is_mine && <span className="text-[10px] v2-gold-text">(إدارتك)</span>}</div>
                    <div className="text-[10px] text-[#bcc8cf]">إنجاز {r.completion_rate}% · مشاركة {r.participation_rate}%</div>
                  </div>
                  <div className="w-24 hidden sm:block"><Bar value={r.score} /></div>
                  <span className="font-extrabold v2-emerald-text w-12 text-end">{r.score}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#bcc8cf] mt-4 leading-relaxed">
              المنافسة تعرض نتائج الإدارات فقط — بيانات موظفي الإدارات الأخرى غير ظاهرة لأي مدير إدارة.
            </p>
          </GlassCard>
        </>
      )}
    </div>
  );
}
